import { Lock, Mail, Phone, User } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button, Checkbox, Field, Input } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { postAuthPath } from '@/lib/pending-invite';
import { useAuth } from '@/store/auth';

import { AuthShell } from './login';

export function RegisterPage() {
  const navigate = useNavigate();
  const register = useAuth((state) => state.register);

  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  function update(key: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: '', _: '' }));
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      await register({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        ...(form.phone.replace(/\D/g, '').length >= 10 ? { phone: form.phone } : {}),
        acceptMarketing,
      });
      navigate(postAuthPath('/'), { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.details?.length) {
        setErrors(caught.fieldErrors);
      } else {
        setErrors({
          _: caught instanceof ApiError ? caught.message : 'Não foi possível criar sua conta.',
        });
      }
    } finally {
      setLoading(false);
    }
  }

  const canSubmit =
    form.name.trim().length >= 2 &&
    form.email.includes('@') &&
    form.password.length >= 8 &&
    acceptedLegal;

  return (
    <AuthShell
      title="Criar conta"
      subtitle="É rápido. Depois é só criar sua pelada ou entrar com um convite."
      footer={
        <>
          Já tem conta?{' '}
          <Link to="/entrar" className="font-semibold text-brand hover:underline">
            Entrar
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Nome completo" htmlFor="name" error={errors.name}>
          <div className="relative">
            <User className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-ink-3" />
            <Input
              id="name"
              autoComplete="name"
              placeholder="Como te chamam no grupo"
              className="pl-9"
              value={form.name}
              onChange={(event) => update('name', event.target.value)}
              invalid={Boolean(errors.name)}
              required
            />
          </div>
        </Field>

        <Field label="E-mail" htmlFor="reg-email" error={errors.email}>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-ink-3" />
            <Input
              id="reg-email"
              type="email"
              autoComplete="email"
              placeholder="voce@email.com"
              className="pl-9"
              value={form.email}
              onChange={(event) => update('email', event.target.value)}
              invalid={Boolean(errors.email)}
              required
            />
          </div>
        </Field>

        <Field
          label="Celular"
          htmlFor="phone"
          hint="Opcional — só para avisos importantes do grupo"
          error={errors.phone}
        >
          <div className="relative">
            <Phone className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-ink-3" />
            <Input
              id="phone"
              type="tel"
              autoComplete="tel"
              placeholder="(11) 98765-4321"
              className="pl-9"
              value={form.phone}
              onChange={(event) => update('phone', event.target.value)}
              invalid={Boolean(errors.phone)}
            />
          </div>
        </Field>

        <Field
          label="Senha"
          htmlFor="reg-password"
          hint="Mínimo de 8 caracteres, com pelo menos uma letra e um número"
          error={errors.password ?? errors._}
        >
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-ink-3" />
            <Input
              id="reg-password"
              type="password"
              autoComplete="new-password"
              placeholder="Crie uma senha"
              className="pl-9"
              value={form.password}
              onChange={(event) => update('password', event.target.value)}
              invalid={Boolean(errors.password)}
              required
            />
          </div>
        </Field>

        {/* Aceites LGPD separados; marketing nunca vem pré-marcado. */}
        <div className="space-y-3 pt-1">
          <Checkbox
            checked={acceptedLegal}
            onChange={setAcceptedLegal}
            label="Li e aceito os Termos de Uso e a Política de Privacidade"
          />
          <Checkbox
            checked={acceptMarketing}
            onChange={setAcceptMarketing}
            label="Quero receber novidades por e-mail (opcional)"
          />
        </div>

        <Button type="submit" size="lg" full loading={loading} disabled={!canSubmit}>
          Criar conta
        </Button>
      </form>
    </AuthShell>
  );
}
